/**
 * Spinner page that creates a fresh room for a given game and redirects
 * the host to `/:gameId/host/:code`. The host's profile is seeded from
 * localStorage if available, falling back to a sensible default.
 */

import { Box, Button, Loader, Stack, Text, Title } from '@mantine/core'
import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { getGameModule } from '../registry'
import type { GameId } from '@mdev/common'
import { createRoom } from '../lobby/service'

export function CreateRoomPage() {
  const { gameId } = useParams<{ gameId: string }>()
  const navigate = useNavigate()

  const [ error, setError ] = useState<string | null>( null )
  const startedRef = useRef( false )

  useEffect( () => {
    if ( startedRef.current ) return
    startedRef.current = true

    if ( !gameId ) {
      setError( 'Missing game id.' )
      return
    }

    const mod = getGameModule( gameId as GameId )
    if ( !mod ) {
      setError( `Unknown game: ${ gameId }` )
      return
    }

    // The host is a display, not a player. Give it its OWN id (prefixed so it
    // can never collide with a player uid) rather than the shared per-device
    // `localStorage` uid. Otherwise a player who joins from the host's own
    // browser would reuse that uid — which the lobby/engine exclude as "the
    // host" — and end up a spectator.
    const hostUid = `host-${
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${ Date.now() }-${ Math.floor( Math.random() * 1e9 ) }`
    }`

    void ( async () => {
      try {
        const { code } = await createRoom( {
          gameId: mod.id,
          hostUid,
          gameSettings: mod.defaultSettings,
          maxPlayers: mod.maxPlayers,
        } )
        navigate( `/games/${ mod.id }/host/${ code }`, { replace: true } )
      } catch ( err ) {
        const message = err instanceof Error ? err.message : 'Failed to create room.'
        setError( message )
      }
    } )()
  }, [ gameId, navigate ] )

  return (
    <Box
      style={ {
        minHeight: '100vh',
        background: '#0B0F1A',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      } }
      p="xl"
    >
      { error ? (
        <Stack
          gap="md"
          align="center"
        >
          <Title
            order={ 2 }
            c="red"
          >
            Couldn't create room
          </Title>
          <Text c="dimmed">{ error }</Text>
          <Button
            component={ Link }
            to="/games"
            variant="light"
          >
            Back to games
          </Button>
        </Stack>
      ) : (
        <Stack
          gap="md"
          align="center"
        >
          <Loader color="yellow" />
          <Text c="dimmed">Creating room…</Text>
        </Stack>
      ) }
    </Box>
  )
}
